import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Grid,
  units,
} from '@ha/dsl';
import { track } from 'ha/modules/Analytics/actions';
import { useIntl } from 'ha/i18n';

const useStyles = makeStyles(theme => ({
  summaryRoot: {
    padding: units.yx(0.3, 1.5),
    minHeight: 'auto',

    '&.Mui-expanded ': {
      minHeight: 'auto',
    },
  },
  summaryContent: {
    margin: 0,

    '&.Mui-expanded ': {
      margin: 0,
    },
  },
  root: {
    borderTop: units.border(1, 'solid', theme.palette.grey[300]),
    boxShadow: 'none',
    '&:before': {
      content: 'none',
    },
  },
  expanded: {
    '&$expanded': {
      margin: 0,
    },
  },
  detailsRoot: {
    paddingTop: 0,
    paddingLeft: units.rem(1.5),
    paddingRight: units.rem(1.5),
  },
  clearButton: {
    textTransform: 'none',
    color: theme.palette.secondary.dark,
  },
}));

interface Props {
  label: string | React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  dataTestLocator: string;
  onClear?: () => void;
}

const SearchFilterAccordion: React.FC<Props> = ({
  label,
  children,
  defaultExpanded = false,
  dataTestLocator,
  onClear,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { T } = useIntl();

  // Needs to be stored only on init and not be updated when the prop change.
  const initExpanded = React.useRef(defaultExpanded);

  const onChange = React.useCallback(
    (event, expanded: boolean) => {
      dispatch(
        track('Search filter accordion clicked', {
          action: expanded ? 'opened' : 'closed',
          filterType: dataTestLocator,
        }),
      );
    },
    [dispatch, dataTestLocator],
  );

  return (
    <Accordion
      square
      defaultExpanded={initExpanded.current}
      classes={{
        root: classes.root,
        expanded: classes.expanded,
      }}
      onChange={onChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
        }}
        data-test-locator={`${dataTestLocator} Filter Accordion`}
      >
        <Grid
          container
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          {React.isValidElement(label) ? (
            label
          ) : (
            <Typography.ParagraphTitle>{label}</Typography.ParagraphTitle>
          )}
          {onClear && (
            <Button
              variant="text"
              color="secondary"
              className={classes.clearButton}
              onClick={onClear}
            >
              {T('cta.clear')}
            </Button>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.detailsRoot }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default SearchFilterAccordion;
